import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Instagram as InstagramIcon, MessageCircle, Send, Loader2 } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { ORG } from "../data/content";
import { openWhatsApp } from "../utils/scroll";

const ease = [0.22, 1, 0.36, 1];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    if (errors[field]) setErrors({ ...errors, [field]: null });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please share your name";
    if (!form.email.trim()) errs.email = "Please share your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "That email doesn't look right";
    if (!form.message.trim()) errs.message = "Please write a short message";
    return errs;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setSending(true);
    const text = [
      "Namaste Hope Giver team!",
      "",
      `Name: ${form.name.trim()}`,
      `Email: ${form.email.trim()}`,
      form.phone.trim() ? `Phone: ${form.phone.trim()}` : null,
      "",
      "Message:",
      form.message.trim(),
    ]
      .filter(Boolean)
      .join("\n");

    toast.success("Opening WhatsApp with your message…", { description: "Just press send and it's on its way." });
    setTimeout(() => {
      openWhatsApp(ORG.whatsappNumber, text);
      setSending(false);
    }, 600);
  };

  const inputCls =
    "w-full rounded-2xl border border-[#0D4B56]/15 bg-sand px-5 py-3.5 text-[15px] text-[#0F172A] placeholder:text-[#94A3B8] transition-all duration-300 focus:border-[#D4AF37] focus:bg-white focus:outline-none focus:ring-4 focus:ring-[#D4AF37]/15";

  return (
    <section id="contact" className="bg-sand py-16 sm:py-24 lg:py-32" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
          {/* Info side */}
          <div>
            <SectionHeading
              eyebrow="Contact"
              title="Get in"
              accent="touch."
              sub="Call us, message on WhatsApp or find us on Instagram — we would love to hear from you."
            />

            <div className="mt-10 space-y-4">
              <motion.a
                href={ORG.phoneTel}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, ease }}
                whileHover={{ x: 6 }}
                data-testid="contact-phone-link"
                className="flex items-center gap-5 rounded-3xl bg-white p-5 shadow-[0_10px_30px_rgba(13,75,86,0.07)] transition-shadow hover:shadow-[0_18px_40px_rgba(13,75,86,0.13)]"
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#E6F0F2] text-[#0D4B56]">
                  <Phone size={24} />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#B59328]">
                    Call us — tap to dial
                  </span>
                  <span className="mt-0.5 block font-display text-2xl font-extrabold text-[#0D4B56]">
                    {ORG.phoneDisplay}
                  </span>
                </span>
              </motion.a>

              <motion.a
                href={ORG.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.1, ease }}
                whileHover={{ x: 6 }}
                data-testid="contact-instagram-link"
                className="flex items-center gap-5 rounded-3xl bg-white p-5 shadow-[0_10px_30px_rgba(13,75,86,0.07)] transition-shadow hover:shadow-[0_18px_40px_rgba(13,75,86,0.13)]"
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#E6F0F2] text-[#0D4B56]">
                  <InstagramIcon size={24} />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#B59328]">Instagram</span>
                  <span className="mt-0.5 block font-display text-2xl font-extrabold text-[#0D4B56]">
                    {ORG.instagramHandle}
                  </span>
                </span>
              </motion.a>

              <motion.a
                href={`https://wa.me/${ORG.whatsappNumber}`}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: 0.2, ease }}
                whileHover={{ x: 6 }}
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-5 rounded-3xl bg-[#0D4B56] p-5 shadow-[0_10px_30px_rgba(13,75,86,0.18)]"
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-[#F3E096]">
                  <MessageCircle size={24} />
                </span>
                <span>
                  <span className="block text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                    Fastest reply
                  </span>
                  <span className="mt-0.5 block font-display text-xl font-extrabold text-white">
                    Chat with us on WhatsApp
                  </span>
                </span>
              </motion.a>
            </div>
          </div>

          {/* Form side */}
          <motion.form
            onSubmit={onSubmit}
            noValidate
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, ease }}
            className="rounded-[2rem] bg-white p-7 shadow-[0_20px_60px_rgba(13,75,86,0.10)] sm:p-10"
            data-testid="contact-form"
          >
            <h3 className="font-display text-2xl font-bold text-[#0D4B56]">Send us a message</h3>
            <p className="mt-2 text-sm text-[#475569]">
              Fill this in and it opens WhatsApp — your message arrives ready to send.
            </p>

            <div className="mt-7 space-y-5">
              <div>
                <label htmlFor="cf-name" className="mb-1.5 block text-sm font-bold text-[#0D4B56]">
                  Name
                </label>
                <input
                  id="cf-name"
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Your full name"
                  data-testid="contact-form-name"
                  aria-invalid={!!errors.name}
                  className={inputCls}
                />
                {errors.name && (
                  <p role="alert" data-testid="contact-form-name-error" className="mt-1.5 text-sm font-semibold text-red-600">
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-email" className="mb-1.5 block text-sm font-bold text-[#0D4B56]">
                    Email
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@example.com"
                    data-testid="contact-form-email"
                    aria-invalid={!!errors.email}
                    className={inputCls}
                  />
                  {errors.email && (
                    <p role="alert" data-testid="contact-form-email-error" className="mt-1.5 text-sm font-semibold text-red-600">
                      {errors.email}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="cf-phone" className="mb-1.5 block text-sm font-bold text-[#0D4B56]">
                    Phone <span className="font-medium text-[#94A3B8]">(optional)</span>
                  </label>
                  <input
                    id="cf-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Your phone number"
                    data-testid="contact-form-phone"
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="cf-message" className="mb-1.5 block text-sm font-bold text-[#0D4B56]">
                  Message
                </label>
                <textarea
                  id="cf-message"
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="How would you like to help, or how can we support you?"
                  data-testid="contact-form-message"
                  aria-invalid={!!errors.message}
                  className={`${inputCls} resize-none`}
                />
                {errors.message && (
                  <p role="alert" data-testid="contact-form-message-error" className="mt-1.5 text-sm font-semibold text-red-600">
                    {errors.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-whatsapp-submit-btn"
                className="group flex w-full items-center justify-center gap-2.5 rounded-full gold-shimmer px-8 py-4 text-base font-bold text-[#08353D] shadow-[0_14px_40px_rgba(212,175,55,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_50px_rgba(212,175,55,0.55)] disabled:cursor-not-allowed disabled:opacity-70"
              >
                {sending ? (
                  <>
                    <Loader2 size={20} className="animate-spin" /> Opening WhatsApp…
                  </>
                ) : (
                  <>
                    <Send size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                    Send via WhatsApp
                  </>
                )}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
